import { useFormik } from 'formik';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Col, Form, FormGroup, Label } from 'reactstrap';
import { InputField } from 'src/components';
import { config } from 'src/config';
import { setFromErrors } from 'src/helpers';
import { useGetBankInformationQuery } from 'src/rtk/features/researchClinicSetup/researchClinicSetupApi';
import { useGetDepartmentsQuery } from 'src/rtk/features/setup/department/departmentApi';
import { useGetOrganizationsQuery } from 'src/rtk/features/setup/organization/setupOrganizationApi';
import { useGetPersonalProfileQuery } from 'src/rtk/features/trainee/traineeApi';
import { useCreateTrainingAllowanceMutation } from 'src/rtk/features/trainingAllowance/trainingAllowanceApi';
import * as Yup from 'yup';
import loader from '../../assets/images/loader.gif';

const initialValues = {
    training_allowance_id: '',
    start_date: '',
    end_date: '',
    name_in_block_letters: '',
    bank_name: '',
    branch_name: '',
    routing_no: '',
    account_no: '',
    submission_status: '',
    attachments: [],
    next_sessions: [],
};

interface Props {
    id?: string;
    setShowEditForm?: any;
    row?: any;
}

const EditAllowanceForm = (props: Props) => {
    const { id, setShowEditForm, row } = props;

    const { data: bankInfo, isSuccess: isBankInfoSuccess, isLoading: isBankInfoLoading } = useGetBankInformationQuery();

    const { data: personalProfile, isError: isDeptFetchError, isLoading: isProfileLoading } = useGetPersonalProfileQuery();

    const { data: instituteLookupData } = useGetOrganizationsQuery();

    const { data: departmentLookupData } = useGetDepartmentsQuery();

    const [createTrainingAllowance, { isSuccess, isError, error, isLoading: isCreateLoading }] = useCreateTrainingAllowanceMutation();

    const [btnClicked, setBtnClicked] = useState<string>('');
    const [allowanceId, setAllowanceId] = useState('');
    const [optionSelected, setOptionSelected] = useState('No');
    const [instituteName, setInstituteName] = useState('');
    const [selectedInstitue, setSelectedInstitute] = useState<any>('');
    const [selectedDepartment, setSelectedDepartment] = useState<any>('');
    const [startDate, setStartDate] = useState<any>('');
    const [endDate, setEndDate] = useState<any>('');
    const [index, setIndex] = useState<number>(0);
    const [plusButtonDisabled, setPlusButtonDisabled] = useState<boolean>(true);

    const [inputList, setInputList] = useState<any>([]);
    const [attachmentList, setAttachmentList] = useState<any>([]);

    const allInstitute: SelectOption[] =
        instituteLookupData?.data?.map((institute) => ({
            value: institute?.id!,
            label: institute.organization_name,
        })) || [];

    const allDepartment: SelectOption[] =
        departmentLookupData?.data?.map((department: any) => ({
            value: department?.id!,
            label: department.department_name,
        })) || [];

    const err: CustomError = error as CustomError;

    const {
        handleSubmit,
        getFieldProps,
        handleChange,
        handleBlur,
        values,
        touched,
        errors,
        resetForm,
        setFieldValue,
        setErrors,
    } = useFormik({
        initialValues,

        validationSchema: Yup.object({
            name_in_block_letters: Yup.string().required('Enter Your Name').matches(/^[A-Z.\s-]+$/, 'Name must be in block letters'),
            bank_name: Yup.string().required('Bank Name is Required'),
            branch_name: Yup.string().required('Branch Name is Required'),
            routing_no: Yup.string().required('Routing No is Required'),
            account_no: Yup.string().min(13, 'Must be 13 digits').matches(/^\d+$/, 'Account No must be numeric').required('Account No is Required'),
            start_date: optionSelected === 'Yes' ? Yup.string().required('Starting Date is Required').nullable() : Yup.string().nullable(),
            end_date: optionSelected === 'Yes' ? Yup.string().required('Ending Date is Required').nullable() : Yup.string().nullable(),
        }),

        onSubmit: (values: any) => {
            const formData = new FormData();
            values.training_allowance_id = id;
            values.next_sessions = [];

            values.submission_status = btnClicked;

            Object.keys(inputList).forEach((key) => {
                values.next_sessions.push(inputList[key]);
            });

            values.attachments = attachmentList;

            Object.keys(values).forEach((key) => {
                if (key === 'attachments') {
                    values.attachments.forEach((item: any, index: number) => {
                        if (typeof item.file === 'string' && item?.file !== '') {
                            formData.append(`attachments[${index}][file_name]`, item.file_name);
                            formData.append(`attachments[${index}][file]`, item.file.replace('storage/uploads/training-allowance-file/', ''));
                        }
                        else if(item.file) {
                            // New file uploaded

                            formData.append(`attachments[${index}][file_name]`, item.file_name);
                            formData.append(`attachments[${index}][file]`, item.file);
                        }
                    });
                }

                if (key === 'next_sessions') {
                    values.next_sessions.forEach((item: any, index: number) => {
                        formData.append(`next_sessions[${index}][institute_id]`, item.institute_id);
                        formData.append(`next_sessions[${index}][department_id]`, item.department_id);
                        formData.append(`next_sessions[${index}][starting_date]`, item.starting_date);
                        formData.append(`next_sessions[${index}][ending_date]`, item.ending_date);
                    });
                }

                if (key === 'submission_status') {
                    if (btnClicked === 'submit') {
                        values[key] = 'Submit';
                    } else if (btnClicked === 'save_as_draft') {
                        values[key] = 'Draft';
                    }
                }

                if (key !== 'attachments' && key !== 'next_sessions' && key !== 'start_date' && key !== 'end_date') {
                    formData.append(key, values[key]);
                }
            });

            if (optionSelected) {
                if (values.start_date) {
                    formData.append('start_date', values.start_date);
                }
                if (values.end_date) {
                    formData.append('end_date', values.end_date);
                }
            }
            createTrainingAllowance(formData);
            console.log(values);
        },
    });

    useEffect(() => {
        if (row) {
            setFieldValue('name_in_block_letters', row?.registration?.personal_info?.full_name.toUpperCase());
            setFieldValue('bank_name', row?.registration?.bank_info?.bank_name);
            setFieldValue('branch_name', row?.registration?.bank_info?.branch_name);
            setFieldValue('routing_no', row?.registration?.bank_info?.routing_no);
            setFieldValue('account_no', row?.registration?.bank_info?.account_no);
        }
    }, [row]);

    useEffect(() => {
        if (isSuccess) {
            setSelectedInstitute([]);
            setSelectedDepartment([]);
            setShowEditForm(false);
            toast.success('Training Allowance Entry Successfully!');
        }
        if (isError) {
            toast.error(err?.message || 'Something Went Wrong!');
            setFromErrors(err.data, setErrors);
            setAttachmentList([]);
        }
    }, [isSuccess, isError]);

    const handleAddFields = () => {
        const values = [...inputList];
        values.push({
            institute_id: selectedInstitue?.value,
            department_id: selectedDepartment?.value,
            starting_date: startDate,
            ending_date: endDate
        });
        setInputList(values);
        setPlusButtonDisabled(true);
        setSelectedInstitute('');
        setSelectedDepartment('');
        setStartDate('');
        setEndDate('');
    };

    const handleRemoveFields = (index: number) => {
        const values = [...inputList];
        values.splice(index, 1);
        setInputList(values);
    };

    const handleInputChange = (name: any, value: any) => {
        if (value !== '' && name === 'starting_date') {
            setStartDate(value);
            const fromDate = new Date(value);
            const newToDate = new Date(fromDate.getFullYear(), fromDate.getMonth() + 6, fromDate.getDate());
            // const values = [...inputList];
            // values[index]['ending_date'] = newToDate?.toISOString().slice(0, 10);
            // setInputList(values);
            setEndDate(newToDate?.toISOString().slice(0, 10))
        }

        // const values = [...inputList];
        // values[index][name] = value;
        // setInputList(values);
    };

    useEffect(() => {
        if (row?.files) {
            const initialAttachments = row.files.map((file: any) => ({
                file_name: file.file_name,
                file: file.file,
            }));
            setAttachmentList(initialAttachments);
        }
    }, [row]);

    
    const handleAttachmentChange = (fileName: string, file: File | null) => {
        if (fileName && file) {
            setAttachmentList((prev: any) => {
                // Check if the attachment already exists in the list
                const existingIndex = prev.findIndex((item: any) => item.file_name === fileName);
                if (existingIndex !== -1) {
                    // Update existing attachment
                    const updatedList = [...prev];
                    updatedList[existingIndex] = {
                        file_name: fileName,
                        file: file,
                    };
                    return updatedList;
                } else {
                    // Add new attachment
                    return [
                        ...prev,
                        {
                            file_name: fileName,
                            file: file,
                        },
                    ];
                }
            });
        }
    };

    const fcpsPartOneInfo = personalProfile?.data?.details?.educational_infos?.find((item: any) => item?.name_of_the_degree === 'FCPS-Part-1');
    const bdsInfo = personalProfile?.data?.details?.educational_infos?.find((item: any) => item?.name_of_the_degree === 'BDS' || item?.name_of_the_degree === 'MBBS');

    useEffect(() => {
        if (selectedInstitue && selectedDepartment && startDate && endDate) {
            setPlusButtonDisabled(false);
        }
    }, [selectedInstitue, selectedDepartment, startDate, endDate]);


    return (
        <React.Fragment>
            {isCreateLoading ? (
                <div className="overlay">
                    <img src={loader} alt="Loading" height={100} width={300} />
                </div>
            ) : null}
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}
                onReset={(e) => {
                    resetForm();
                    setOptionSelected('');
                    setFieldValue('attachments', []);
                }}
            >
                <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                    <Label sm={6} lg={6} md={6}>Are you selected or continuing the residency training/diploma course/ Govt. service/Private service?</Label>
                    <Col sm={6} lg={6} md={6} className='mt-3'>
                        <div className="d-inline-block mx-2">
                            <div className="form-check form-check-left mb-3">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="selected"
                                    id="optionYes"
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setOptionSelected('Yes');
                                        }
                                    }}
                                    value={"Yes"}
                                    checked={optionSelected === 'Yes'}
                                />
                                <label className="form-check-label" htmlFor="optionYes">Yes</label>
                            </div>
                        </div>

                        <div className="d-inline-block mx-2">
                            <div className="form-check form-check-left">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="selected"
                                    id="optionNo"
                                    value={"No"}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setOptionSelected('No');
                                        }
                                    }}
                                    checked={optionSelected === 'No'}
                                />
                                <label className="form-check-label" htmlFor="optionNo">No</label>
                            </div>
                        </div>
                    </Col>
                </FormGroup>

                {optionSelected === 'Yes' &&
                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label for="start_date" className="form-label required-field" sm={3} lg={3} md={3}>Starting Date</Label>
                        <Col sm={3} lg={3} md={3}>
                            <InputField
                                type="date"
                                name='start_date'
                                onChange={(e: any) => {
                                    handleChange(e);
                                }}
                                onBlur={handleBlur}
                                invalid={{ errors, touched }}
                            />
                        </Col>

                        <Label for="end_date" className="form-label required-field" sm={3} lg={3} md={3}>Ending Date</Label>
                        <Col sm={3} lg={3} md={3}>
                            <InputField
                                type="date"
                                name='end_date'
                                onChange={(e: any) => {
                                    handleChange(e);
                                }}
                                onBlur={handleBlur}
                                invalid={{ errors, touched }}
                            />
                        </Col>
                    </FormGroup>}
                {/* <Table className='table table-bordered table-striped table-sm mb-0'>
                    <thead>
                        <tr className='bg-light'>
                            <td colSpan={6}><h5>Mention the name of the institutes with department recognized by BCPS according to your choice where you
                                want to obtain the fellowship training: (Please schedule the rest of training Including FCPS course and excluding current duration)
                            </h5></td>
                        </tr>
                        <tr>
                            <th rowSpan={2} className='col-lg-3'>Name of the Institutes</th>
                            <th rowSpan={2} className='col-lg-3'>Name of the Department</th>
                            <th colSpan={2} className="text-center">Duration of training</th>
                        </tr>

                        <tr>
                            <th className='col-lg-3'>Starting date</th>
                            <th className='col-lg-2'>Ending date</th>
                            <th className='col-lg-1'>Action</th>
                        </tr>
                    </thead>
                </Table>
                <div key={index} className='border p-2'>
                    <Row>
                        <Col lg={3}>
                            
                            <Select
                                isClearable={true}
                                isSearchable={true}
                                // value={allInstitute.find(
                                //     (option: any) =>
                                //         option.value === selectedInstitue,
                                // )}
                                onChange={setSelectedInstitute}
                                value={selectedInstitue}
                                options={allInstitute}
                                placeholder='Select Institute'
                                name="institute_id"
                            />
                        </Col>
                        <Col lg={3}>
                            
                            <Select
                                isClearable={true}
                                isSearchable={true}
                                // value={allDepartment.find(
                                //     (option: any) =>
                                //         option.value === selectedDepartment,
                                // )}
                                onChange={setSelectedDepartment}
                                value={selectedDepartment}
                                options={allDepartment}
                                placeholder='Select Department'
                                name="department_id"
                            />
                        </Col>
                        <Col lg={3}>
                            <InputField
                                type="date"
                                name='starting_date'
                                onChange={(event: any) => {
                                    handleInputChange(event?.target?.name, event?.target?.value)
                                }}
                                value={startDate}
                                onBlur={handleBlur}
                                invalid={{ errors, touched }}
                                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                            />
                        </Col>
                        <Col lg={2}>
                            <InputField
                                type="date"
                                name='ending_date'
                                onChange={(event: any) => {
                                    handleInputChange(event?.target?.name, event?.target?.value)
                                }}
                                value={endDate}
                                onBlur={handleBlur}
                                invalid={{ errors, touched }}
                                disabled
                            />
                        </Col>
                        <Col lg={1}>
                            <div className='text-end my-2'>
                                <button
                                    type="button"
                                    className={'btn btn-sm btn-primary'}
                                    onClick={handleAddFields}
                                    disabled={plusButtonDisabled}
                                >
                                    <i className="fa fa-plus-circle" />
                                </button>
                            </div>
                        </Col>
                    </Row>
                </div> */}
                <div>
                    <table className='table table-bordered table-sm'>
                        <tbody>
                            {inputList.map((input: any, index: any) => (
                                <tr key={index}>
                                    <td className='col-3'>{allInstitute?.find((item: any) => item?.value === input?.institute_id)?.label}</td>
                                    <td className='col-3'>{allDepartment?.find((item: any) => item?.value === input?.department_id)?.label}</td>
                                    <td className='col-3'>{moment(input?.starting_date).format('DD-MMM-YYYY')}</td>
                                    <td className='col-2'>{moment(input?.ending_date).format('DD-MMM-YYYY')}</td>
                                    <td className='col-1 text-end'>
                                        <button
                                            type="button"
                                            className={'btn btn-sm btn-danger'}
                                            onClick={() => handleRemoveFields(index)}
                                        >
                                            <i className="fa fa-minus-circle" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <fieldset className="border p-2">
                    <legend className='w-auto' style={{ float: 'none', padding: 'inherit', fontSize: 20, fontWeight: 'bold' }}>Applicant's Personal Bank Information:</legend>

                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label sm={6} lg={6} md={6} for="name_in_block_letters" className="form-label required-field">Name in block letters (as per Bank Account)</Label>
                        <Col sm={6} lg={6} md={6}>
                            <InputField
                                type="text"
                                placeholder='Name in block letters (as per Bank Account)'
                                {...getFieldProps('name_in_block_letters')}
                                invalid={{ errors, touched }}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label sm={6} lg={6} md={6} for="bank_name" className="form-label required-field">Name of the bank</Label>
                        <Col sm={6} lg={6} md={6}>
                            <InputField
                                type="text"
                                placeholder="Name of the bank"
                                {...getFieldProps('bank_name')}
                                invalid={{ errors, touched }}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label sm={6} lg={6} md={6} for="branch_name" className="form-label required-field">Name of the branch</Label>
                        <Col sm={6} lg={6} md={6}>
                            <InputField
                                type="text"
                                placeholder="Name of the branch"
                                {...getFieldProps('branch_name')}
                                invalid={{ errors, touched }}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label sm={6} lg={6} md={6} for="routing_no" className="form-label required-field">Routing Number</Label>
                        <Col sm={6} lg={6} md={6}>
                            <InputField
                                type="text"
                                placeholder="Routing Number"
                                {...getFieldProps('routing_no')}
                                invalid={{ errors, touched }}
                            />
                        </Col>
                    </FormGroup>

                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label sm={6} lg={6} md={6} for="account_no" className="form-label required-field">Account Number (13 digits or above)</Label>
                        <Col sm={6} lg={6} md={6}>
                            <InputField
                                type="text"
                                placeholder="Account Number (13 digits or above)"
                                {...getFieldProps('account_no')}
                                invalid={{ errors, touched }}
                            />
                        </Col>
                    </FormGroup>
                </fieldset>
                <fieldset className="border p-2">
                    <legend className='w-auto' style={{ float: 'none', padding: 'inherit', fontSize: 20, fontWeight: 'bold' }}>Enclosures: (The applicants have to scan and upload the following documents)</legend>
                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label sm={6} lg={6} md={6} for="applicant_signature" className="form-label">Applicant’s Signature: </Label>
                        {personalProfile?.data?.details?.personal_info?.signature ?
                            <Col sm={6} lg={6} md={6}>
                                <div>
                                    <a
                                        href={config.APP_URL + personalProfile?.data?.details?.personal_info?.signature}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        <p className="btn btn-sm btn-outline-primary w-100">
                                            View Applicant’s Signature
                                        </p>
                                    </a>
                                </div>
                            </Col>
                            :
                            <Col sm={6} lg={6} md={6}>
                                <input
                                    name="applicant_signature"
                                    type="file"
                                    className="form-control"
                                    placeholder="Upload Certificate"
                                    onChange={(e: any) => {
                                        handleAttachmentChange('applicant_signature', e.target.files[0]);
                                    }}
                                    
                                >
                                </input>
                            </Col>}
                    </FormGroup>
                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label sm={6} lg={6} md={6} for="recent_photograph" className="form-label">1. Recent passport size color photograph :</Label>
                        {personalProfile?.data?.details?.personal_info?.picture ?
                            <Col sm={6} lg={6} md={6}>
                                <div>
                                    <a
                                        href={config.APP_URL + personalProfile?.data?.details?.personal_info?.picture}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        <p className="btn btn-sm btn-outline-primary w-100">
                                            View Applicant’s Photograph
                                        </p>
                                    </a>
                                </div>
                            </Col>
                            :
                            <Col sm={6} lg={6} md={6}>
                                <input
                                    name="recent_photograph"
                                    type="file"
                                    className="form-control"
                                    placeholder="Upload Certificate"
                                    onChange={(e: any) => {
                                        handleAttachmentChange('recent_photograph', e.target.files[0]);
                                    }}
                                    
                                >
                                </input>
                            </Col>}
                    </FormGroup>
                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label sm={6} lg={6} md={6} for="congratulation_letter" className="form-label">2. Congratulation letter of FCPS Part-I/ FCPS Part-I passed document: </Label>
                        {fcpsPartOneInfo?.certificate_pdf ?
                            <Col sm={6} lg={6} md={6}>
                                <div>
                                    <a
                                        href={config.APP_URL + fcpsPartOneInfo?.certificate_pdf}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        <p className="btn btn-sm btn-outline-primary w-100">
                                            View FCPS Part-1 Document
                                        </p>
                                    </a>
                                </div>
                            </Col>
                            :
                            <Col sm={6} lg={6} md={6}>
                                <input
                                    name="congratulation_letter"
                                    type="file"
                                    className="form-control"
                                    placeholder="Upload Certificate"
                                    onChange={(e: any) => {
                                        handleAttachmentChange('congratulation_letter', e.target.files[0]);
                                    }}
                                    
                                >
                                </input>
                            </Col>}
                    </FormGroup>
                    <FormGroup row className="col-sm-12 col-md-12 col-lg-12">
                        <Label sm={4} lg={4} md={4} for="mbbs_certificate_pdf" className="form-label">3. Certificate of MBBS/BDS: </Label>
                        {bdsInfo?.certificate_pdf ?
                            <Col sm={4} lg={4} md={4}>
                                <div>
                                    <a
                                        href={config.APP_URL + bdsInfo?.certificate_pdf}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        <p className="btn btn-sm btn-outline-primary w-100">
                                            View BDS Certificate
                                        </p>
                                    </a>
                                </div>
                            </Col>
                            :
                            <Col sm={4} lg={4} md={4}>
                                <input
                                    name="mbbs_certificate_pdf"
                                    type="file"
                                    className="form-control"
                                    placeholder="Upload Certificate"
                                    onChange={(e: any) => {
                                        handleAttachmentChange('mbbs_certificate_pdf', e.target.files[0]);
                                    }}
                                    
                                >
                                </input>
                            </Col>}
                             <Col sm={4} lg={4} md={4}>
                            {
                                row?.files?.find((item: any) => item.file_name === 'mbbs_certificate_pdf') ?
                                    <span>
                                        <a
                                            href={config.APP_URL + row?.files?.find((item: any) => item.file_name === 'mbbs_certificate_pdf')?.file}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            <p className="btn btn-sm btn-outline-primary w-100">
                                                View MBBS/BDS Certificate
                                            </p>
                                        </a>
                                    </span>
                                    :
                                    null
                            }  </Col>
                    </FormGroup>
                    <FormGroup row className="col-sm-12 col-md-8 col-lg-8">
                        <Label sm={6} lg={6} md={6} for="permanent_reg_certificate" className="form-label">4. Permanent registration certificate of BMDC: </Label>
                        {personalProfile?.data?.details?.bmdc_certificate ?
                            <Col sm={6} lg={6} md={6}>
                                <div>
                                    <a
                                        href={config.APP_URL + personalProfile?.data?.details?.bmdc_certificate}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        <p className="btn btn-sm btn-outline-primary w-100">
                                            View BMDC Certificate
                                        </p>
                                    </a>
                                </div>
                            </Col>
                            :
                            <Col sm={6} lg={6} md={6}>
                                <input
                                    name="permanent_reg_certificate"
                                    type="file"
                                    className="form-control"
                                    placeholder="Upload Certificate"
                                    onChange={(e: any) => {
                                        handleAttachmentChange('permanent_reg_certificate', e.target.files[0]);
                                    }}
                                    
                                >
                                </input>
                            </Col>}
                    </FormGroup>
                    <FormGroup row className="col-sm-12 col-md-12 col-lg-12">
                        <Label sm={4} lg={4} md={4} for="training_certifcate" className="form-label">5. Provisional Training certificates (if applicable): </Label>
                        <Col sm={4} lg={4} md={4}>
                            <input
                                name="training_certifcate"
                                type="file"
                                className="form-control"
                                placeholder="Upload Certificate"
                                onChange={(e: any) => {
                                    handleAttachmentChange('training_certifcate', e.target.files[0]);
                                }}
                                
                            >
                            </input>
                            
                        </Col>
                        <Col sm={4} lg={4} md={4}>
                              {
                                row?.files?.find((item: any) => item.file_name === 'training_certifcate') ?
                                    <span>
                                        <a
                                            href={config.APP_URL + row?.files?.find((item: any) => item.file_name === 'training_certifcate')?.file}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            <p className="btn btn-sm btn-outline-primary w-100">
                                                View Training Certificate
                                            </p>
                                        </a>
                                    </span>
                                    :
                                    null
                            }  
                        </Col>
                        
                    </FormGroup>
                    <FormGroup row className="col-sm-12 col-md-12 col-lg-12">
                        <Label sm={4} lg={4} md={4} for="bank_cheque" className="form-label">6. A page of the Bank Cheque book of the applicant : </Label>
                        <Col sm={4} lg={4} md={4}>
                            <input
                                name="bank_cheque"
                                type="file"
                                className="form-control"
                                placeholder="Upload Certificate"
                                onChange={(e: any) => {
                                    handleAttachmentChange('bank_cheque', e.target.files[0]);
                                }}
                                
                            >
                            </input>
                        </Col>
                        <Col sm={4} lg={4} md={4}>
                              {
                                row?.files?.find((item: any) => item.file_name === 'bank_cheque') ?
                                    <span>
                                        <a
                                            href={config.APP_URL + row?.files?.find((item: any) => item.file_name === 'bank_cheque')?.file}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            <p className="btn btn-sm btn-outline-primary w-100">
                                                View Bank Cheque
                                            </p>
                                        </a>
                                    </span>
                                    :
                                    null
                            }  
                        </Col>
                    </FormGroup>
                    <FormGroup row className="col-sm-12 col-md-12 col-lg-12">
                        <Label sm={4} lg={4} md={4} for="national_id" className="form-label">7. National ID card: </Label>
                        <Col sm={4} lg={4} md={4}>
                            <input
                                name="national_id"
                                type="file"
                                className="form-control"
                                placeholder="Upload Certificate"
                                onChange={(e: any) => {
                                    handleAttachmentChange('national_id', e.target.files[0]);
                                }}
                                
                            >
                            </input>
                        </Col>
                        <Col sm={4} lg={4} md={4}>
                              {
                                row?.files?.find((item: any) => item.file_name === 'national_id') ?
                                    <span>
                                        <a
                                            href={config.APP_URL + row?.files?.find((item: any) => item.file_name === 'national_id')?.file}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            <p className="btn btn-sm btn-outline-primary w-100">
                                                View National ID
                                            </p>
                                        </a>
                                    </span>
                                    :
                                    null
                            }  
                        </Col>
                    </FormGroup>
                    <FormGroup row className="col-sm-12 col-md-12 col-lg-12">
                        <Label sm={4} lg={4} md={4} for="testimoninal" className="form-label">8. Joining letter/ Testimonial: </Label>
                        <Col sm={4} lg={4} md={4}>
                            <input
                                name="testimoninal"
                                type="file"
                                className="form-control"
                                placeholder="Upload Certificate"
                                onChange={(e: any) => {
                                    handleAttachmentChange('testimoninal', e.target.files[0]);
                                }}
                                
                            >
                            </input>
                        </Col>
                        <Col sm={4} lg={4} md={4}>
                              {
                                row?.files?.find((item: any) => item.file_name === 'testimoninal') ?
                                    <span>
                                        <a
                                            href={config.APP_URL + row?.files?.find((item: any) => item.file_name === 'testimoninal')?.file}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            <p className="btn btn-sm btn-outline-primary w-100">
                                                View Joining Letter
                                            </p>
                                        </a>
                                    </span>
                                    :
                                    null
                            }  
                        </Col>
                    </FormGroup>
                    <FormGroup row className="col-sm-12 col-md-12 col-lg-12">
                        <Label sm={4} lg={4} md={4} for="others_document" className="form-label">9. Other necessary documents: </Label>
                        <Col sm={4} lg={4} md={4}>
                            <input
                                name="others_document"
                                type="file"
                                className="form-control"
                                placeholder="Upload Certificate"
                                onChange={(e: any) => {
                                    handleAttachmentChange('others_document', e.target.files[0]);
                                }}
                            // required
                            >
                            </input>
                        </Col>
                        <Col sm={4} lg={4} md={4}>
                              {
                                row?.files?.find((item: any) => item.file_name === 'others_document') ?
                                    <span>
                                        <a
                                            href={config.APP_URL + row?.files?.find((item: any) => item.file_name === 'others_document')?.file}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            <p className="btn btn-sm btn-outline-primary w-100">
                                                View Other Document
                                            </p>
                                        </a>
                                    </span>
                                    :
                                    null
                            }  
                        </Col>
                    </FormGroup>
                </fieldset>
                <FormGroup className="my-3 text-center">
                    <Button
                        type="reset"
                        className="btn btn-danger me-2"
                    >
                        Reset
                    </Button>
                    {/* <button
                        type="submit"
                        className="btn btn-info me-2"
                        // ref={draftButtonRef}
                        onClick={(e) => {
                            setBtnClicked('save_as_draft');
                        }}
                        disabled={optionSelected === 'Yes'}
                    >
                        Save as Draft
                    </button> */}
                    <button
                        type="submit"
                        // ref={buttonRef}
                        className="btn btn-success"
                        onClick={(e) => {
                            setBtnClicked('submit');
                        }}
                        disabled={optionSelected === 'Yes'}
                    >
                        Submit
                    </button>
                </FormGroup>
            </Form>
        </React.Fragment >
    );
};

export default EditAllowanceForm;
